import { useState, useEffect, useRef } from 'react';

interface ListeningSession {
  id: string;
  startTime: Date;
  endTime?: Date;
  duration: number; // in seconds
  date: string; // YYYY-MM-DD format
}

interface DailyStats {
  date: string;
  totalTime: number; // in seconds
  sessions: number;
}

interface WeeklyStats {
  week: string; // YYYY-WW format
  totalTime: number;
  sessions: number;
  averageDaily: number;
}

interface ListeningStats {
  totalListeningTime: number; // in seconds
  totalSessions: number;
  currentStreak: number; // days
  longestStreak: number; // days
  averageSessionLength: number; // in seconds
  dailyStats: DailyStats[];
  weeklyStats: WeeklyStats[];
  favoriteTimeOfDay: string;
  thisWeekTime: number;
  lastWeekTime: number;
  thisMonthTime: number;
}

export function useListeningStats() {
  const [stats, setStats] = useState<ListeningStats>({
    totalListeningTime: 0,
    totalSessions: 0,
    currentStreak: 0,
    longestStreak: 0,
    averageSessionLength: 0,
    dailyStats: [],
    weeklyStats: [],
    favoriteTimeOfDay: 'Evening',
    thisWeekTime: 0,
    lastWeekTime: 0,
    thisMonthTime: 0,
  });

  const currentSessionRef = useRef<ListeningSession | null>(null);
  const updateIntervalRef = useRef<number>();

  const getStorageKey = (key: string) => `playfm_stats_${key}`;

  const loadStats = () => {
    try {
      const savedStats = localStorage.getItem(getStorageKey('listening_stats'));
      if (savedStats) {
        const parsed = JSON.parse(savedStats);
        setStats(parsed);
      }
    } catch (error) {
      console.error('Error loading listening stats:', error);
    }
  };

  const saveStats = (newStats: ListeningStats) => {
    try {
      localStorage.setItem(getStorageKey('listening_stats'), JSON.stringify(newStats));
      setStats(newStats);
    } catch (error) {
      console.error('Error saving listening stats:', error);
    }
  };

  const calculateStreaks = (dailyStats: DailyStats[]): { current: number; longest: number } => {
    if (dailyStats.length === 0) return { current: 0, longest: 0 };

    const sortedStats = [...dailyStats].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;
    
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    // Check if user listened today or yesterday for current streak
    const latestDate = sortedStats[0]?.date;
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    if (latestDate === todayStr || latestDate === yesterdayStr) {
      currentStreak = 1;
      
      // Count consecutive days backwards
      for (let i = 1; i < sortedStats.length; i++) {
        const currentDate = new Date(sortedStats[i-1].date);
        const nextDate = new Date(sortedStats[i].date);
        const dayDiff = Math.floor((currentDate.getTime() - nextDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (dayDiff === 1) {
          currentStreak++;
        } else {
          break;
        }
      }
    }
    
    // Calculate longest streak
    for (let i = 0; i < sortedStats.length; i++) {
      tempStreak = 1;
      
      for (let j = i + 1; j < sortedStats.length; j++) {
        const currentDate = new Date(sortedStats[j-1].date);
        const nextDate = new Date(sortedStats[j].date);
        const dayDiff = Math.floor((currentDate.getTime() - nextDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (dayDiff === 1) {
          tempStreak++;
        } else {
          break;
        }
      }
      
      longestStreak = Math.max(longestStreak, tempStreak);
    }
    
    return { current: currentStreak, longest: longestStreak };
  };

  const getFavoriteTimeOfDay = (sessions: ListeningSession[]): string => {
    const timeSlots = { Morning: 0, Afternoon: 0, Evening: 0, Night: 0 };
    
    sessions.forEach(session => {
      const hour = session.startTime.getHours();
      if (hour >= 6 && hour < 12) timeSlots.Morning++;
      else if (hour >= 12 && hour < 18) timeSlots.Afternoon++;
      else if (hour >= 18 && hour < 22) timeSlots.Evening++;
      else timeSlots.Night++;
    });
    
    return Object.entries(timeSlots).reduce((a, b) => timeSlots[a[0]] > timeSlots[b[0]] ? a : b)[0];
  };

  const getWeekNumber = (date: Date): string => {
    const year = date.getFullYear();
    const firstDayOfYear = new Date(year, 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    return `${year}-${weekNumber.toString().padStart(2, '0')}`;
  };

  const startSession = () => {
    if (currentSessionRef.current) return; // Session already active

    const now = new Date();
    currentSessionRef.current = {
      id: `session_${now.getTime()}`,
      startTime: now,
      duration: 0,
      date: now.toISOString().split('T')[0],
    };

    // Update duration every second
    updateIntervalRef.current = window.setInterval(() => {
      if (currentSessionRef.current) {
        currentSessionRef.current.duration = Math.floor(
          (Date.now() - currentSessionRef.current.startTime.getTime()) / 1000
        );
      }
    }, 1000);
  };

  const endSession = () => {
    if (!currentSessionRef.current) return;

    const session = currentSessionRef.current;
    session.endTime = new Date();
    session.duration = Math.floor(
      (session.endTime.getTime() - session.startTime.getTime()) / 1000
    );

    // Only save sessions longer than 10 seconds
    if (session.duration >= 10) {
      const sessions = JSON.parse(localStorage.getItem(getStorageKey('sessions')) || '[]');
      sessions.push(session);
      localStorage.setItem(getStorageKey('sessions'), JSON.stringify(sessions));
      
      updateStats();
    }

    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
    }
    currentSessionRef.current = null;
  };

  const updateStats = () => {
    try {
      const sessions: ListeningSession[] = JSON.parse(
        localStorage.getItem(getStorageKey('sessions')) || '[]'
      ).map((s: any) => ({
        ...s,
        startTime: new Date(s.startTime),
        endTime: s.endTime ? new Date(s.endTime) : undefined,
      }));

      if (sessions.length === 0) return;

      // Calculate daily stats
      const dailyStatsMap = new Map<string, DailyStats>();
      sessions.forEach(session => {
        const date = session.date;
        if (!dailyStatsMap.has(date)) {
          dailyStatsMap.set(date, { date, totalTime: 0, sessions: 0 });
        }
        const dayStats = dailyStatsMap.get(date)!;
        dayStats.totalTime += session.duration;
        dayStats.sessions++;
      });

      const dailyStats = Array.from(dailyStatsMap.values()).sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      // Calculate weekly stats
      const weeklyStatsMap = new Map<string, WeeklyStats>();
      sessions.forEach(session => {
        const week = getWeekNumber(session.startTime);
        if (!weeklyStatsMap.has(week)) {
          weeklyStatsMap.set(week, { week, totalTime: 0, sessions: 0, averageDaily: 0 });
        }
        const weekStats = weeklyStatsMap.get(week)!;
        weekStats.totalTime += session.duration;
        weekStats.sessions++;
      });

      // Calculate average daily for each week
      weeklyStatsMap.forEach(weekStats => {
        weekStats.averageDaily = weekStats.totalTime / 7;
      });

      const weeklyStats = Array.from(weeklyStatsMap.values()).sort(
        (a, b) => b.week.localeCompare(a.week)
      );

      // Calculate time periods
      const now = new Date();
      const thisWeek = getWeekNumber(now);
      const lastWeek = getWeekNumber(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();

      const thisWeekTime = weeklyStats.find(w => w.week === thisWeek)?.totalTime || 0;
      const lastWeekTime = weeklyStats.find(w => w.week === lastWeek)?.totalTime || 0;
      const thisMonthTime = sessions
        .filter(s => {
          const sessionDate = new Date(s.startTime);
          return sessionDate.getMonth() === thisMonth && sessionDate.getFullYear() === thisYear;
        })
        .reduce((total, s) => total + s.duration, 0);

      // Calculate other stats
      const totalListeningTime = sessions.reduce((total, s) => total + s.duration, 0);
      const totalSessions = sessions.length;
      const averageSessionLength = totalListeningTime / totalSessions;
      const streaks = calculateStreaks(dailyStats);
      const favoriteTimeOfDay = getFavoriteTimeOfDay(sessions);

      const newStats: ListeningStats = {
        totalListeningTime,
        totalSessions,
        currentStreak: streaks.current,
        longestStreak: streaks.longest,
        averageSessionLength,
        dailyStats,
        weeklyStats,
        favoriteTimeOfDay,
        thisWeekTime,
        lastWeekTime,
        thisMonthTime,
      };

      saveStats(newStats);
    } catch (error) {
      console.error('Error updating stats:', error);
    }
  };

  const getCurrentSessionDuration = (): number => {
    return currentSessionRef.current?.duration || 0;
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  useEffect(() => {
    loadStats();
    
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, []);

  return {
    stats,
    startSession,
    endSession,
    getCurrentSessionDuration,
    formatDuration,
    updateStats,
  };
}